import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { api, fmtRelative } from "../../lib/api";
import { Plus, RotateCcw, Check } from "lucide-react";
import { Kpi, Th, Td, Pill, SectionTitle, Loading, STATUS_STYLE, errMsg, usd, num } from "./shared";

const BLANK = {
  name: "", enabled: true, apply_when: "once_available", apply_method: "in_place",
  min_cpu_change_pct: 10, min_mem_change_pct: 10, min_monthly_savings_usd: 5,
  directions: ["down"], baseline_percentile: "p95", overhead_margin_pct: 25,
  cpu_floor_cores: 0.05, mem_floor_mib: 64, max_decrease_pct: 30,
  apply_on_hpa: true, gitops_mode: "pr", auto_attach_labels: [], exclude_preliminary: true, window: "any",
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

export const KubeAutomation = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [acked, setAcked] = useState({});

  const load = () => {
    setData(null);
    api.kube.automation(clusterId).then(setData).catch(console.error);
  };
  useEffect(load, [clusterId]);

  const save = async () => {
    try {
      const payload = { ...editing, cluster_id: clusterId };
      await api.kube.saveRule(payload);
      toast.success(`Rule "${payload.name}" saved`, { description: "Matching workloads will be evaluated on the next cycle." });
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save the rule", { description: errMsg(e) });
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Remove the rule "${rule.name}"? Workloads attached to it stop being optimized.`)) return;
    try {
      await api.kube.deleteRule(rule.id);
      toast.success(`Rule "${rule.name}" removed`);
      load();
    } catch (e) {
      toast.error("Could not remove the rule", { description: errMsg(e) });
    }
  };

  const ack = async (rb) => {
    try {
      await api.kube.ackRollback(rb.id);
      setAcked((a) => ({ ...a, [rb.id]: true }));
      toast.success(`Rollback ${rb.changeset_id} acknowledged`, { description: rb.remediation });
      load();
    } catch (e) {
      toast.error("Could not acknowledge the rollback", { description: errMsg(e) });
    }
  };

  if (!data) return <Loading label="Loading automation…" testId="kube-automation-loading" />;
  const c = data.counts;

  return (
    <div className="space-y-6" data-testid="kube-automation">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi testId="kube-auto-attached" label="Attached to a rule" value={`${data.attached_pct}%`} sub={`${data.total_workloads} workloads total`} />
        <Kpi testId="kube-auto-optimized" label="Optimized" value={num(c.optimized)} accent="text-[#15803d]" />
        <Kpi testId="kube-auto-partial" label="Partial" value={num(c.partial)} accent="text-[#92400e]" />
        <Kpi testId="kube-auto-rollback" label="Rolled back" value={num(c.rollback)} sub="each with its cause" accent="text-[#b91c1c]" />
        <Kpi testId="kube-auto-notattached" label="Not attached" value={num(c.not_attached)} sub="coverage gap" />
        <Kpi testId="kube-auto-rules" label="Active rules" value={num(data.rules.filter((r) => r.enabled).length)} />
      </div>

      <div className="cs-card p-5" data-testid="kube-rules">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <SectionTitle hint="Windows, apply path, floors, ranges, margin and HPA handling — the same controls the optimizer enforces.">Automation rules</SectionTitle>
          <button data-testid="kube-new-rule" onClick={() => setEditing({ ...BLANK, name: "New rule" })}
            className="px-3 py-1.5 rounded-sm bg-[#00579e] text-white text-xs hover:bg-[#014a87] transition-colors inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New rule
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr><Th>Rule</Th><Th>When</Th><Th>Apply method</Th><Th>Baseline</Th><Th right>Margin</Th><Th right>Min change</Th><Th>HPA</Th><Th>Auto-attach</Th><Th right>Matched</Th><Th /></tr>
            </thead>
            <tbody>
              {data.rules.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`kube-rule-${r.id}`}>
                  <Td className="font-medium">{r.name} {!r.enabled && <span className="text-muted-foreground">(paused)</span>}</Td>
                  <Td className="text-muted-foreground">{r.apply_when === "once_available" ? "Once available" : r.window}</Td>
                  <Td mono>{r.apply_method === "in_place" ? "in-place resize" : "rolling restart"}</Td>
                  <Td mono>{r.baseline_percentile.toUpperCase()}</Td>
                  <Td right mono>+{r.overhead_margin_pct}%</Td>
                  <Td right mono>{r.min_cpu_change_pct}% / {r.min_mem_change_pct}%</Td>
                  <Td>{r.apply_on_hpa ? <span className="text-[#6d28d9]">coupled</span> : <span className="text-muted-foreground">skipped</span>}</Td>
                  <Td mono className="text-muted-foreground">{r.auto_attach_labels?.join(", ") || "—"}</Td>
                  <Td right mono>{r.matched_workloads}</Td>
                  <Td right>
                    <button className="text-[#00579e] hover:underline" data-testid={`kube-edit-rule-${r.id}`}
                      onClick={() => setEditing({ ...r })}>edit</button>
                    <button className="text-[#b91c1c] hover:underline ml-2" data-testid={`kube-delete-rule-${r.id}`}
                      onClick={() => removeRule(r)}>remove</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="kube-auto-attach-suggestions">
          {data.suggested_auto_attach.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-dashed border-border text-[11px]">
              <span className="cs-mono">{s.label}</span>
              <span className="text-muted-foreground">{s.workloads} workloads · {usd(s.potential_monthly_savings_usd)}/mo</span>
              <button className="text-[#00579e] hover:underline" data-testid={`kube-attach-${s.label}`}
                onClick={() => setEditing({ ...BLANK, name: `Auto-attach ${s.label}`, auto_attach_labels: [s.label] })}>attach</button>
            </span>
          ))}
        </div>
      </div>

      {!!data.rollbacks.length && (
        <div className="cs-card p-5" data-testid="kube-rollbacks">
          <SectionTitle hint="Ocean counts rollbacks; ASTRA shows the signal that caused each one and what it changed.">
            Rollbacks · explained
          </SectionTitle>
          <div className="space-y-2.5">
            {data.rollbacks.map((rb) => (
              <div key={rb.id} className="rounded-sm border border-border p-3.5" data-testid={`kube-rollback-${rb.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <Pill cls="bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]">{rb.signal.replace("_", " ")}</Pill>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold">{rb.namespace}/{rb.workload}
                      <span className="ml-2 cs-mono text-[10px] text-muted-foreground">{rb.changeset_id}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{rb.signal_detail}</div>
                    <div className="text-xs mt-1"><span className="text-muted-foreground">Remediation: </span>{rb.remediation}</div>
                  </div>
                  <div className="ml-auto text-right shrink-0">
                    <div className="cs-mono text-[10px] text-muted-foreground">{fmtRelative(rb.rolled_back_at)} · {rb.time_to_rollback_sec}s to revert</div>
                    <button disabled={rb.acknowledged || acked[rb.id]} onClick={() => ack(rb)} data-testid={`kube-ack-${rb.id}`}
                      className="mt-1 px-2.5 py-1 rounded-sm border border-border text-[11px] hover:bg-[hsl(var(--cs-tint))] disabled:opacity-50 transition-colors inline-flex items-center gap-1">
                      {rb.acknowledged || acked[rb.id] ? <><Check className="w-3 h-3" /> acknowledged</> : <><RotateCcw className="w-3 h-3" /> acknowledge</>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cs-card p-5" data-testid="kube-workload-status">
        <SectionTitle hint="Coverage is the adoption problem — attach by label to close it.">Workload automation status</SectionTitle>
        <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr><Th>Workload</Th><Th>Namespace</Th><Th>Class</Th><Th>HPA</Th><Th>Status</Th><Th right>Margin</Th><Th right>Potential / mo</Th></tr></thead>
            <tbody>
              {data.workloads.map((w) => (
                <tr key={w.id} className="border-t border-border" data-testid={`kube-status-${w.workload}`}>
                  <Td className="font-medium">{w.workload}</Td>
                  <Td className="text-muted-foreground">{w.namespace}</Td>
                  <Td mono>{w.workload_class}</Td>
                  <Td>{w.hpa ? "on" : "off"}</Td>
                  <Td><Pill cls={STATUS_STYLE[w.status].cls}>{STATUS_STYLE[w.status].label}</Pill></Td>
                  <Td right mono>+{w.margin_pct}%</Td>
                  <Td right mono className="font-semibold">{usd(w.potential_monthly_savings_usd)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="kube-rule-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="cs-heading text-base">Automation rule</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Rule name">
                  <Input className="h-8 text-xs" data-testid="kube-rule-name" value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </Field>
                <Field label="When to apply">
                  <Select value={editing.apply_when} onValueChange={(v) => setEditing({ ...editing, apply_when: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-rule-when"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once_available">Once available</SelectItem>
                      <SelectItem value="specific_time">Specific window</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Apply method" hint="In-place resize needs Kubernetes 1.33+ and avoids a restart.">
                  <Select value={editing.apply_method} onValueChange={(v) => setEditing({ ...editing, apply_method: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-rule-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_place">Update workloads live (in-place resize)</SelectItem>
                      <SelectItem value="rolling_restart">Rolling restart</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Window" hint="Used when 'Specific window' is selected.">
                  <Input className="h-8 text-xs" data-testid="kube-rule-window" value={editing.window}
                    onChange={(e) => setEditing({ ...editing, window: e.target.value })} />
                </Field>
                <Field label="Recommendation baseline">
                  <Select value={editing.baseline_percentile} onValueChange={(v) => setEditing({ ...editing, baseline_percentile: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-rule-baseline"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["p99", "p95", "p90", "p85", "avg"].map((p) => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Overhead margin %" hint="Added on top of the baseline and shown on every recommendation.">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-margin" value={editing.overhead_margin_pct}
                    onChange={(e) => setEditing({ ...editing, overhead_margin_pct: Number(e.target.value) })} />
                </Field>
                <Field label="Minimum CPU change %">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-mincpu" value={editing.min_cpu_change_pct}
                    onChange={(e) => setEditing({ ...editing, min_cpu_change_pct: Number(e.target.value) })} />
                </Field>
                <Field label="Minimum memory change %">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-minmem" value={editing.min_mem_change_pct}
                    onChange={(e) => setEditing({ ...editing, min_mem_change_pct: Number(e.target.value) })} />
                </Field>
                <Field label="CPU floor (cores)">
                  <Input type="number" step="0.01" className="h-8 text-xs" data-testid="kube-rule-cpufloor" value={editing.cpu_floor_cores}
                    onChange={(e) => setEditing({ ...editing, cpu_floor_cores: Number(e.target.value) })} />
                </Field>
                <Field label="Memory floor (MiB)">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-memfloor" value={editing.mem_floor_mib}
                    onChange={(e) => setEditing({ ...editing, mem_floor_mib: Number(e.target.value) })} />
                </Field>
                <Field label="Max decrease per cycle %" hint="Large cuts are stepped over several cycles.">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-maxdec" value={editing.max_decrease_pct}
                    onChange={(e) => setEditing({ ...editing, max_decrease_pct: Number(e.target.value) })} />
                </Field>
                <Field label="Minimum monthly saving ($)" hint="Tenant-level threshold — most container findings are small but add up.">
                  <Input type="number" className="h-8 text-xs" data-testid="kube-rule-minsave" value={editing.min_monthly_savings_usd}
                    onChange={(e) => setEditing({ ...editing, min_monthly_savings_usd: Number(e.target.value) })} />
                </Field>
                <Field label="When a change is approved" hint="Overrides the default GitOps behaviour for workloads on this rule.">
                  <Select value={editing.gitops_mode || "pr"} onValueChange={(v) => setEditing({ ...editing, gitops_mode: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-rule-gitops"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pr" data-testid="kube-rule-gitops-pr">Open a pull request instead of applying</SelectItem>
                      <SelectItem value="live" data-testid="kube-rule-gitops-live">Apply live only</SelectItem>
                      <SelectItem value="live_and_pr" data-testid="kube-rule-gitops-both">Apply live and open a pull request</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Auto-attach labels" hint="Comma separated, e.g. workload-class=batch">
                  <Input className="h-8 text-xs" data-testid="kube-rule-labels" value={(editing.auto_attach_labels || []).join(", ")}
                    onChange={(e) => setEditing({ ...editing, auto_attach_labels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                </Field>
                <div className="space-y-2.5">
                  {[["enabled", "Rule enabled"], ["apply_on_hpa", "Apply on HPA-associated workloads"],
                    ["exclude_preliminary", "Exclude preliminary recommendations"]].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs">{label}</span>
                      <Switch checked={!!editing[key]} data-testid={`kube-rule-${key}`}
                        onCheckedChange={(v) => setEditing({ ...editing, [key]: v })} />
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Allow scale-ups (reliability)</span>
                    <Switch checked={editing.directions?.includes("up")} data-testid="kube-rule-dir-up"
                      onCheckedChange={(v) => setEditing({ ...editing, directions: v ? ["down", "up"] : ["down"] })} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button data-testid="kube-rule-save" onClick={save}
                  className="px-3 py-1.5 rounded-sm bg-[#00579e] text-white text-xs hover:bg-[#014a87] transition-colors">Save rule</button>
                <button onClick={() => setEditing(null)}
                  className="px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-[hsl(var(--cs-tint))] transition-colors">Cancel</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KubeAutomation;
